/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, FormControl, Grid, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { LAST_NAMES, NAME, PHONE_NUMBER, EMAIL } from "@utils/expreciones";
import { useFormik } from "formik";
import * as yup from "yup";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { useLazyGetPersonsQuery, usePatchPersonMutation, usePostPersonAvalAditionalMutation } from "@api/personasApi";
import { useNavigate } from "react-router-dom";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";

const InformacionSegundoAval = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const segundoAvalId = useSelector((state: RootState) => state.bofu.aval_adicional_id_information);

  const [patchPerson] = usePatchPersonMutation();
  const [getPersons] = useLazyGetPersonsQuery();
  const [postPersonAvalAditional] = usePostPersonAvalAditionalMutation();

  const [isLoading, setIsLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: "",
      last_name: "",
      last_name_2: "",
      email: "",
      country_code: "+52",
      phone: "",
    },
    validationSchema: yup.object().shape({
      name: yup.string().required("Campo requerido").matches(new RegExp(NAME), "Nombre inválido, revisa el formato"),
      last_name: yup
        .string()
        .required("Campo requerido")
        .matches(new RegExp(LAST_NAMES), "Apellido paterno inválido, revisa el formato"),
      last_name_2: yup.string().matches(new RegExp(LAST_NAMES), "Apellido materno inválido, revisa el formato"),
      email: yup.string().required("Campo requerido").matches(EMAIL, "Correo electrónico inválido"),
      country_code: yup.string().required("Campo requerido"),
      phone: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!segundoAvalId) {
          return;
        }

        setSubmitting(true);

        await patchPerson({
          personId: segundoAvalId,
          body: {
            name: values.name,
            last_name: values.last_name,
            last_name_2: values.last_name_2,
            email: values.email,
            code_phone_number: values.country_code,
            phone_number: values.phone,
            information_person_completed: true,
            personal_information_status: "COMPLETO",
          },
        }).unwrap();

        await postPersonAvalAditional({
          body: {
            loan_application_id: applicationData.id,
            person_id: segundoAvalId,
            name: values.name,
            last_name: values.last_name,
            last_name_2: values.last_name_2,
            code_phone_number: values.country_code,
            phone_number: values.phone,
            email: values.email,
          },
        }).unwrap();

        navigate("/producto/creditosimple/docs_aval");
      } catch (error) {
        const api_error = error as ApiError;

        if (api_error.status === 409) {
          formik.setFieldError("phone", "El número de teléfono ya se encuentra registrado para otro aval");
          return;
        }

        Sentry.captureException(error, {
          extra: {
            component: "InformacionSegundoAval-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        if (!segundoAvalId) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        const res_persons = await getPersons({
          loanApplicationId: applicationData.id,
          id: segundoAvalId,
        }).unwrap();

        const second_aval = res_persons[0];

        if (!second_aval) {
          navigate("/producto/creditosimple/docs_aval");
          return;
        }

        formik.setFieldValue("name", second_aval.name);
        formik.setFieldValue("last_name", second_aval.last_name);
        formik.setFieldValue("last_name_2", second_aval.last_name_2);
        formik.setFieldValue("email", second_aval.email);
        formik.setFieldValue("country_code", second_aval.code_phone_number || "+52");
        formik.setFieldValue("phone", second_aval.phone_number || "");

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "InformacionSegundoAval-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box maxWidth="sm" mx="auto" mt={4} px={2}>
          <Typography
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
          >
            Solicitud de Aval Adicional
          </Typography>

          <Typography
            mt={2}
            textAlign="center"
            variant={isMobileOrTablet ? "body2" : "body1"}
            color={theme?.palette?.primary?.main}
            fontWeight={600}
          >
            Te solicitamos ingresar un segundo aval a tu solicitud....
          </Typography>

          <form onSubmit={formik.handleSubmit} noValidate>
            <FormControl fullWidth>
              <TextField
                id="name"
                name="name"
                label="Nombre"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                value={formik.values.name}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="last_name"
                name="last_name"
                label="Apellido paterno"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                helperText={formik.touched.last_name && formik.errors.last_name}
                value={formik.values.last_name}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="last_name_2"
                name="last_name_2"
                label="Apellido materno"
                margin="normal"
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={formik.touched.last_name_2 && Boolean(formik.errors.last_name_2)}
                helperText={formik.touched.last_name_2 && formik.errors.last_name_2}
                value={formik.values.last_name_2}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <FormControl fullWidth>
              <TextField
                id="email"
                name="email"
                label="Correo electrónico"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const value_to_set = (e.target.value || "").toLowerCase();
                  formik.setFieldValue("email", value_to_set);
                }}
                value={formik.values.email}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email ? formik.errors.email : ""}
                inputProps={{ maxLength: 100 }}
                disabled={isLoading || formik.isSubmitting}
              />
            </FormControl>

            <Grid container spacing={1} mt={1}>
              <Grid item xs={4} sm={4} md={4}>
                <CustomContryCode
                  label="Código"
                  name="country_code"
                  value={formik.values.country_code}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>

              <Grid item xs={8} sm={8} md={8}>
                <TextField
                  type="text"
                  name="phone"
                  variant="outlined"
                  label="Teléfono"
                  fullWidth
                  required
                  value={formik.values.phone}
                  onChange={(event) => {
                    formik.setFieldValue("phone", event.target.value.replace(/[^0-9]/g, ""));
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                  }}
                  disabled={isLoading || formik.isSubmitting}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1} my={1}>
              <Grid item xs={6} sm={6} md={6}>
                <Button
                  variant="blue_outlined"
                  type="button"
                  fullWidth
                  onClick={() => {
                    navigate("/producto/creditosimple/docs_aval");
                  }}
                  disabled={isLoading || formik.isSubmitting}
                  sx={{ padding: "0px", height: "100%" }}
                >
                  Regresar al inicio
                </Button>
              </Grid>

              <Grid item xs={6} sm={6} md={6}>
                <Button variant="default" type="submit" fullWidth disabled={!formik.isValid || formik.isSubmitting}>
                  Continuar
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default InformacionSegundoAval;
